import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ExerciseSummary from '../../components/exerciseSummary';
import WeekCalendar from '../../components/charts/donutChart/weeklyCalendar';
import Divider from '../../components/divider';
import DataItem from '../../components/dataItem';
import CommonCard from '../../components/card/CommonCard';
import ChartSection from '../../components/charts/chartSection';
import BarChart from '../../components/charts/barChart';
import ContentArea from '../../components/contentArea';
import { formatDateTime, calculatePercentage } from '../../utils/base';
import { physicalRecordAPI } from '../../actions/imsServer';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';

export const ReportActivity = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });

    // API 데이터 상태
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 사용자 정보 가져오기
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    const handleDayClick = (date) => {
        if (date) {
            const newDate = new Date(date);
            newDate.setHours(0, 0, 0, 0);
            setSelectedDate(newDate);
        }
    }

    // 신체 기록 데이터 가져오기
    const fetchPhysicalRecords = async (startDate, endDate, isInitialLoad = false) => {
        if (!meInfo.id) return;

        try {
            if (isInitialLoad) {
                setLoading(true);
            }
            setError(null);

            const startStr = formatDateTime(startDate, '-', ':', 0, 10);
            const endStr = formatDateTime(endDate, '-', ':', 0, 10);
            let allRecords = [];
            let offset = 0;
            let page;
            do {
                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: meInfo.id,
                    state: 0,
                    startDate: startStr,
                    endDate: endStr,
                    offset
                });
                page = response?.data?.physicalRecords || [];
                allRecords = allRecords.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allRecords.length > 0) {
                // 기존 데이터와 병합 (중복 제거)
                setPhysicalRecords(prevRecords => {
                    const existingDates = new Set(prevRecords.map(r => formatDateTime(new Date(r.date), '-', ':', 0, 10)));
                    const uniqueNewRecords = allRecords.filter(r =>
                        !existingDates.has(formatDateTime(new Date(r.date), '-', ':', 0, 10))
                    );
                    return [...prevRecords, ...uniqueNewRecords].sort((a, b) =>
                        new Date(a.date) - new Date(b.date)
                    );
                });
            }
        } catch (err) {
            console.error('신체 기록 데이터 로딩 실패:', err);
            setError(err.message);
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    };

    // 초기 데이터 로드 (컴포넌트 마운트 시)
    useEffect(() => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드

        fetchPhysicalRecords(startDate, today, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPhysicalRecords는 의존성에서 제외
    }, [meInfo.id]);

    // 스크롤로 새로운 구간 로드
    const loadMoreData = (direction) => {
        if (physicalRecords.length === 0) return;

        const sortedRecords = [...physicalRecords].sort((a, b) => new Date(a.date) - new Date(b.date));
        const earliestDate = new Date(sortedRecords[0].date);
        const latestDate = new Date(sortedRecords[sortedRecords.length - 1].date);

        if (direction === 'past') {
            // 과거 데이터 더 로드
            const newStartDate = new Date(earliestDate);
            newStartDate.setDate(newStartDate.getDate() - 14);
            const newEndDate = new Date(earliestDate);
            newEndDate.setDate(newEndDate.getDate() - 1);

            fetchPhysicalRecords(newStartDate, newEndDate, false);
        } else if (direction === 'future') {
            // 미래 데이터 더 로드 (오늘까지만)
            const today = new Date();
            const newStartDate = new Date(latestDate);
            newStartDate.setDate(newStartDate.getDate() + 1);
            const newEndDate = new Date(Math.min(today.getTime(), latestDate.getTime() + 14 * 24 * 60 * 60 * 1000));

            if (newStartDate <= today) {
                fetchPhysicalRecords(newStartDate, newEndDate, false);
            }
        }
    };

    // 시간대별 차트 데이터 생성 (실제 데이터 기반)
    const generateHourlyData = () => {
        const selectedDateData = physicalRecords.find(record => {
            return formatDateTime(record.date, '.', ':', 0, 10) === formatDateTime(selectedDate, '.', ':', 0, 10);
        });

        if (!selectedDateData) {
            return {
                steps: Array(24).fill(0),
                calories: Array(24).fill(0),
                times: Array(24).fill(0)
            };
        }

        // 시간대별 데이터 초기화
        const steps = Array(24).fill(0);
        const calories = Array(24).fill(0);
        const times = Array(24).fill(0);

        // hourlySteps가 문자열 형태의 배열인 경우 파싱
        if (selectedDateData?.hourlySteps) {
            let hourlyStepsArray;

            // 문자열인 경우 JSON 파싱
            if (typeof selectedDateData.hourlySteps === 'string') {
                try {
                    hourlyStepsArray = JSON.parse(selectedDateData.hourlySteps);
                } catch (e) {
                    console.error('hourlySteps 파싱 실패:', e);
                    hourlyStepsArray = [];
                }
            } else {
                hourlyStepsArray = selectedDateData.hourlySteps;
            }

            // 파싱된 배열을 시간대별 데이터에 적용
            if (Array.isArray(hourlyStepsArray) && hourlyStepsArray.length > 0) {
                hourlyStepsArray.forEach((step, index) => {
                    if (index < 24) {
                        steps[index] = parseInt(step) || 0;
                    }
                });
            }
        }

        // hourlyCalories 처리
        if (selectedDateData?.hourlyCalories) {
            let hourlyCaloriesArray;

            if (typeof selectedDateData.hourlyCalories === 'string') {
                try {
                    hourlyCaloriesArray = JSON.parse(selectedDateData.hourlyCalories);
                } catch (e) {
                    console.error('hourlyCalories 파싱 실패:', e);
                    hourlyCaloriesArray = [];
                }
            } else {
                hourlyCaloriesArray = selectedDateData.hourlyCalories;
            }

            if (Array.isArray(hourlyCaloriesArray) && hourlyCaloriesArray.length > 0) {
                hourlyCaloriesArray.forEach((calorie, index) => {
                    if (index < 24) {
                        calories[index] = parseInt(calorie) || 0;
                    }
                });
            }
        }

        // hourlyTimes 처리
        if (selectedDateData?.hourlyTimes) {
            let hourlyTimeArray;

            if (typeof selectedDateData.hourlyTimes === 'string') {
                try {
                    hourlyTimeArray = JSON.parse(selectedDateData.hourlyTimes);
                } catch (e) {
                    console.error('hourlyTime 파싱 실패:', e);
                    hourlyTimeArray = [];
                }
            } else {
                hourlyTimeArray = selectedDateData.hourlyTimes;
            }

            if (Array.isArray(hourlyTimeArray) && hourlyTimeArray.length > 0) {
                hourlyTimeArray.forEach((time, index) => {
                    if (index < 24) {
                        times[index] = parseInt(time) || 0;
                    }
                });
            }
        }

        return { steps, calories, times };
    };

    const hourlyData = useMemo(() => generateHourlyData(), [physicalRecords, selectedDate, generateHourlyData]);

    // WeekCalendar용 전체 날짜 데이터 생성 (1년치 고정)
    const allDaysData = useMemo(() => {
        const data = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364);
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);
            date.setHours(0, 0, 0, 0);

            const isSelected = date.toDateString() === selectedDate.toDateString();

            // 해당 날짜의 실제 데이터 찾기
            const dayRecord = physicalRecords.find(record => {
                const recordDate = new Date(record.date);
                recordDate.setHours(0, 0, 0, 0);
                return recordDate.toDateString() === date.toDateString();
            });

            const chartData = [
                {
                    label: t('didim'),
                    value: calculatePercentage(dayRecord?.totalSteps || 0, EXERCISE_TARGETS.STEPS),
                    color: theme.palette.figma.steps,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    label: t('calories'),
                    value: calculatePercentage(dayRecord?.calories || 0, EXERCISE_TARGETS.CALORIES),
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    label: t('time'),
                    value: calculatePercentage(dayRecord?.totalTime || 0, EXERCISE_TARGETS.ACTIVITY_TIME),
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];

            data.push({
                day: dayNames[date.getDay()],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
            });
        }

        return data;
    }, [physicalRecords, selectedDate, theme, t]);

    // Generate this week's dates dynamically with real data
    const generateThisWeek = () => {
        const currentDay = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Get start of week (Sunday) based on selectedDate
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - currentDay);

        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
        const week = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);

            const isSelected = date.toDateString() === selectedDate.toDateString();

            // 해당 날짜의 실제 데이터 찾기
            const dayRecord = physicalRecords.find(record => {
                const recordDate = new Date(record.date);
                return recordDate.toDateString() === date.toDateString();
            });

            const chartData = [
                {
                    label: t('didim'),
                    value: calculatePercentage(dayRecord?.totalSteps || 0, EXERCISE_TARGETS.STEPS),
                    color: theme.palette.figma.steps,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    label: t('calories'),
                    value: calculatePercentage(dayRecord?.calories || 0, EXERCISE_TARGETS.CALORIES),
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    label: t('time'),
                    value: calculatePercentage(dayRecord?.totalTime || 0, EXERCISE_TARGETS.ACTIVITY_TIME),
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];

            week.push({
                day: dayNames[i],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
            });
        }

        return week;
    };

    const days = useMemo(() => generateThisWeek(), [selectedDate, physicalRecords, t, generateThisWeek]);

    // Exercise data
    const exerciseData = useMemo(() => {
        // physicalRecords에서 해당 날짜의 데이터 찾기
        const selectedDateStr = formatDateTime(selectedDate, '.', ':', 0, 10);
        const selectedDateData = physicalRecords.find(record => {
            const recordDateStr = formatDateTime(record.date, '.', ':', 0, 10);
            return recordDateStr === selectedDateStr;
        });

        // 실제 데이터 또는 기본값 사용
        const stepCount = selectedDateData?.totalSteps || 0;
        const calories = selectedDateData?.calories || 0;
        const time = selectedDateData?.totalTime || 0; // 분 단위
        const totalDistance = selectedDateData?.distance || 0; // 거리

        return {
            stepCount: stepCount,
            calories: calories,
            time: time,
            totalDistance: totalDistance
        };
    }, [physicalRecords, selectedDate]);

    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">{t('loading_data')}</Typography>
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="error">
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </Typography>
                </Box>
            )}

            {/* Content */}
            {!loading && !error && (
                <>
                    {/* 주간 달력 (스크롤 가능) - 화면 좌우 100% */}
                    <WeekCalendar
                        days={days}
                        onDayClick={handleDayClick}
                        enableScroll={true}
                        fullWidth={true}
                        allDaysData={allDaysData}
                        isLoading={false}
                        size="medium"
                        selectedDate={selectedDate}
                        onScrollToEnd={(direction) => {
                            loadMoreData(direction);
                        }}
                    />

                    {/* 카드 영역만 좌우 여백 - ContentArea에 좌우 padding이 없어 동일한 여백을 위해 px 지정 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', px: '10px' }}>
                        {/* Summary Card */}
                        <CommonCard>
                            <ExerciseSummary
                                totalSteps={exerciseData.stepCount}
                                calories={exerciseData.calories}
                                totalTime={exerciseData.time}
                            />

                            <Divider
                                lineColor={theme.palette.figma.white[20]}
                            />

                            {/* Additional Stats */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '5px',
                                    width: '100%',
                                }}
                            >
                                <DataItem
                                    label={t('distance_by_activity')}
                                    value={exerciseData.totalDistance}
                                    unit="km"
                                />
                            </Box>
                        </CommonCard>

                        {/* Charts Card */}
                        <CommonCard sx={{ gap: '20px' }}>
                            {/* Steps Chart */}
                            <ChartSection
                                title={t('step_count')}
                                renderChart={<BarChart data={hourlyData.steps} color={theme.palette.figma.steps} />}
                            />

                            <Divider
                                lineColor={theme.palette.figma.white[20]}
                            />

                            {/* Calories Chart */}
                            <ChartSection
                                title={t('activity_calories')}
                                renderChart={<BarChart data={hourlyData.calories} color={theme.palette.figma.calorieRed} />}
                            />

                            <Divider
                                lineColor={theme.palette.figma.white[20]}
                            />

                            {/* Time Chart */}
                            <ChartSection
                                title={t('activity_time')}
                                renderChart={<BarChart data={hourlyData.times} color={theme.palette.figma.time} />}
                            />
                        </CommonCard>
                    </Box>
                </>
            )}
        </ContentArea>
    );
};
